import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Arrow } from "../components/Layout";

const methodCards = [
  {
    title: "Manual",
    lead: "Best individual truth",
    body: "Direct, interpretable and physically grounded. Its constraint is not truth, but how many birds can realistically be handled and how often.",
    tone: "manual",
  },
  {
    title: "Scale",
    lead: "Strong individual measurement",
    body: "Automated weighing increases frequency, while the sample still depends on which animals interact with the device.",
    tone: "scale",
  },
  {
    title: "Camera",
    lead: "Population-scale observation",
    body: "Model-dependent at the individual level, but capable of making much larger repeated samples operational without handling the flock.",
    tone: "camera",
  },
];

const populationDots = Array.from({ length: 42 }, (_, index) => index);
const sampleDots = Array.from({ length: 24 }, (_, index) => index);

export default function HomePage() {
  return (
    <div className="page page-home">
      <section className="hero page-section">
        <div className="hero-copy">
          <p className="eyebrow">Agroadvisor · Birdwatcher · Field measurement</p>
          <h1>Measure the flock, not just the bird.</h1>
          <p className="hero-intro">
            Birdwatcher is an Agroadvisor project for continuous poultry population measurement. The model matters, but so do sampling, deployment, drift, calibration and whether the final number still represents the flock.
          </p>
          <div className="hero-actions">
            <Link className="button button-primary" to="/why-it-matters">Why it matters <Arrow /></Link>
            <a className="button button-secondary" href="mailto:info@agroadvisor.hu?subject=Birdwatcher%20private%20demo">Request a private demo</a>
          </div>
          <div className="hero-proofline">
            <span>Field deployed</span>
            <span>Calibration aware</span>
            <span>Sampling focused</span>
          </div>
        </div>

        <motion.div
          className="sampling-story"
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.65, ease: [0.22, 1, 0.36, 1] }}
          aria-label="Population to observed sample to flock estimate"
        >
          <div className="story-depth-label">One measurement problem, three layers</div>

          <motion.div
            className="story-card population-card"
            animate={{ y: [0, -2, 0] }}
            transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
          >
            <div className="story-card-head">
              <span>01</span>
              <strong>Population</strong>
              <em>What is actually there</em>
            </div>
            <div className="dot-field" aria-hidden="true">
              {populationDots.map((dot) => <i key={dot} className={dot % 7 === 0 ? "dot-alt" : ""} />)}
            </div>
            <p>The biological reality we want to understand.</p>
          </motion.div>

          <motion.div
            className="story-card sample-card"
            animate={{ y: [0, -4, 0] }}
            transition={{ duration: 6, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
          >
            <div className="story-card-head">
              <span>02</span>
              <strong>Observed sample</strong>
              <em>50 → 300 → 1,000</em>
            </div>
            <div className="dot-field sample-dot-field" aria-hidden="true">
              {sampleDots.map((dot) => <i key={dot} className={dot % 4 === 0 ? "is-highlighted" : ""} />)}
            </div>
            <p>What we actually get to measure, repeatedly.</p>
          </motion.div>

          <motion.div
            className="story-card estimate-card"
            animate={{ y: [0, -3, 0] }}
            transition={{ duration: 6.5, repeat: Infinity, ease: "easeInOut", delay: 1 }}
          >
            <div className="story-card-head">
              <span>03</span>
              <strong>Flock estimate</strong>
              <em>Mean + uncertainty</em>
            </div>
            <div className="estimate-line" aria-hidden="true">
              <span className="estimate-range" />
              <span className="estimate-mean" />
            </div>
            <div className="estimate-values">
              <span>Estimate</span>
              <strong>2.50 kg</strong>
              <small>± uncertainty</small>
            </div>
          </motion.div>
        </motion.div>
      </section>

      <section className="page-section compact-section">
        <div className="section-label">01 · One project, one question</div>
        <div className="split-intro">
          <h2>How much of the flock do you actually know?</h2>
          <div>
            <p>Manual measurements and scales can be better than cameras at measuring an individual bird. We do not try to hide that.</p>
            <p>The more difficult question is whether a precise individual measurement is representative of tens of thousands of animals.</p>
          </div>
        </div>
      </section>

      <section className="page-section method-preview">
        <div className="method-preview-grid">
          {methodCards.map((item, index) => (
            <motion.article
              className={`method-mini-card method-${item.tone}`}
              key={item.title}
              whileHover={{ y: -4 }}
              transition={{ type: "spring", stiffness: 240, damping: 22 }}
            >
              <div className="method-card-top">
                <span>0{index + 1}</span>
                <i aria-hidden="true" />
              </div>
              <h3>{item.title}</h3>
              <strong>{item.lead}</strong>
              <p>{item.body}</p>
            </motion.article>
          ))}
        </div>
        <div className="method-thesis-card">
          <span className="soft-chip">The useful comparison</span>
          <p>The competition is not “which instrument is best”. It is how measurement error, sampling error and field drift combine into the final decision.</p>
        </div>
      </section>

      <section className="page-section insight-callout">
        <div>
          <p className="eyebrow">Our position</p>
          <h2>Good results are useful. Understanding why they fail is more valuable.</h2>
        </div>
        <div>
          <p>Genetics change. Buildings differ. Lighting, density and behaviour shift. A model can slowly become wrong while its original validation remains impressive.</p>
          <p>We focus on deployment, rapid calibration and making system error visible enough to learn from it.</p>
          <Link className="text-link" to="/approach">See our approach <Arrow /></Link>
        </div>
      </section>

      <section className="page-section next-page-card">
        <span>Next · 02</span>
        <div>
          <h2>Perfect measurements can still give the wrong flock picture.</h2>
          <p>See why sampling changes the meaning of accuracy.</p>
        </div>
        <Link className="round-link" to="/why-it-matters" aria-label="Go to Why it matters">→</Link>
      </section>
    </div>
  );
}
